import type { SvgIconComponent } from "@mui/icons-material";
import BoltOutlinedIcon from "@mui/icons-material/BoltOutlined";
import type { enJSONKeys } from "@/i18n/en/types";
import type { MetaChipTone } from "@/ui/Badges/MetaChip";
import type { SharedChangeFilters } from "./changeFilters";
import { SCHEDULE_CHANGE_ACTOR_KINDS, type ScheduleChangeAction, type ScheduleChangeActorKind, type ScheduleChangesWindowId } from "./types";

/**
 * The glyph for "InHouse did this on its own" (auto-approve, an HRIS sync, the system). The bolt, not the sparkle: the
 * app already marks auto-posted requests with it (`RequestData/RequestOrigin/requestOriginMeta`), while the sparkle
 * means the AI assistant (`ChangeSourceBadge`).
 */
export const AUTOMATION_ICON: SvgIconComponent = BoltOutlinedIcon;

/** Root of every schedule-changes query key — invalidate `[SCHEDULE_CHANGES_QUERY_KEY_ROOT]` after a change lands. */
export const SCHEDULE_CHANGES_QUERY_KEY_ROOT = "schedule-changes";

/** How long a fetched set of changes is fresh. The tab is a glance, not a ledger: a minute behind is fine. */
export const SCHEDULE_CHANGES_STALE_TIME_MS = 60_000;

/**
 * The changes feed polls so a scheduler who keeps the page open still sees the schedule move — every 5 minutes, and
 * only while the tab is visible (a background tab catches up on focus), and only while the side panel or a schedule
 * view that shows changes is up (`useScheduleChangesFeedConsumer`).
 */
export const SCHEDULE_CHANGES_REFETCH_INTERVAL_MS = 300_000;

/**
 * Session-scoped choices — whose changes and what kinds of change the side panel's Changes tab lists: they survive a
 * refresh and a move between pages, and reset when the browser tab closes.
 */
export const WHO_FILTER_SESSION_KEY = "scheduleChanges.whoFilter";
export const ACTION_FILTER_SESSION_KEY = "scheduleChanges.actionFilter";

/**
 * The who filter on a new session: everyone — By me, By others and By system — until the scheduler narrows it.
 */
export const DEFAULT_WHO_FILTER: readonly ScheduleChangeActorKind[] = SCHEDULE_CHANGE_ACTOR_KINDS;

/** The window a session opens on: since my last visit — the question this feature answers. */
export const DEFAULT_WINDOW_ID: ScheduleChangesWindowId = "sinceLastVisit";

/**
 * The "What changed" filter on a new session: shifts added and removed — what moves coverage. The activity log can't
 * tell a change of hours from any other edit, so time changes arrive as `edited`, which is one click away.
 */
export const DEFAULT_ACTION_FILTER: readonly ScheduleChangeAction[] = ["added", "removed"];

/** The shared filters on a new session — what Reset goes back to, and what "filters are set" is measured against. */
export const DEFAULT_SHARED_FILTERS: SharedChangeFilters = { who: DEFAULT_WHO_FILTER, actions: DEFAULT_ACTION_FILTER, windowId: DEFAULT_WINDOW_ID };

/**
 * Each action's tone: what was gained reads green, what was lost red, leave info, a large update a warning (it may
 * have moved anything), every other edit neutral.
 */
export const ACTION_TONE: Readonly<Record<ScheduleChangeAction, MetaChipTone>> = {
	added: "success",
	removed: "error",
	edited: "neutral",
	call_off: "error",
	time_off: "info",
	swapped: "neutral",
	status_changed: "neutral",
	bulk_update: "warning",
};

export const ACTION_LABEL_KEY: Readonly<Record<ScheduleChangeAction, enJSONKeys>> = {
	added: "scheduleChanges.action.added",
	removed: "scheduleChanges.action.removed",
	edited: "scheduleChanges.action.edited",
	call_off: "scheduleChanges.action.callOff",
	time_off: "scheduleChanges.action.timeOff",
	swapped: "scheduleChanges.action.swapped",
	status_changed: "scheduleChanges.action.statusChanged",
	bulk_update: "scheduleChanges.action.bulkUpdate",
};

/** "By me", "By others", "By system" — the who filter's options, in the Changes tab's filters. */
export const WHO_LABEL_KEY: Readonly<Record<ScheduleChangeActorKind, enJSONKeys>> = {
	me: "scheduleChanges.panel.who.me",
	other: "scheduleChanges.panel.who.other",
	system: "scheduleChanges.panel.who.system",
};

/** The same, shortened for a closed picker's summary after "Who:" — "Who: Me, Others". */
export const WHO_SUMMARY_LABEL_KEY: Readonly<Record<ScheduleChangeActorKind, enJSONKeys>> = {
	me: "scheduleChanges.filters.whoShort.me",
	other: "scheduleChanges.filters.whoShort.other",
	system: "scheduleChanges.filters.whoShort.system",
};

/** The window picker's labels, in `ScheduleChangesWindowId` order. */
export const WINDOW_LABEL_KEY: Readonly<Record<ScheduleChangesWindowId, enJSONKeys>> = {
	sinceLastVisit: "scheduleChanges.window.sinceLastVisit",
	last2Days: "scheduleChanges.window.last2Days",
	lastWeek: "scheduleChanges.window.lastWeek",
};
